import React, { useRef, useState } from "react";
import { api } from "@/AuthContext";
import { toast } from "sonner";
import { Buildings, UploadSimple, Trash } from "@phosphor-icons/react";

const MAX_BYTES = 2 * 1024 * 1024;
const TYPES = ["image/png", "image/jpeg", "image/webp"];

// Logos subidos se sirven desde el backend (/api/...); URLs externas heredadas se usan tal cual
export const resolveLogoUrl = (url) =>
  url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

export const CompanyLogo = ({ url, className = "", size = 32 }) => (
  <div className={`rounded-md bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center overflow-hidden ${className}`}>
    {url ? <img src={resolveLogoUrl(url)} className="w-full h-full object-contain bg-white" alt="logo" data-testid="company-logo-img"/> : <Buildings size={size} weight="duotone"/>}
  </div>
);

export default function LogoUploader({ companyId, url, canEdit, onChange, hidePreview = false }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!TYPES.includes(file.type)) { toast.error("Formato no permitido. Usa PNG, JPG o WEBP"); return; }
    if (file.size > MAX_BYTES) { toast.error("El logo supera 2 MB"); return; }
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/companies/${companyId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.logo_url); toast.success("Logo actualizado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir el logo"); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("¿Quitar el logo de la empresa?")) return;
    setBusy(true);
    try { await api.delete(`/companies/${companyId}/logo`); onChange(""); toast.success("Logo eliminado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-5" data-testid="company-logo-uploader">
      {!hidePreview && <CompanyLogo url={url} className="w-20 h-20 border border-neutral-200"/>}
      {canEdit && (
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <button type="button" onClick={() => input.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50" data-testid="company-logo-upload-btn">
              <UploadSimple size={14} weight="bold"/> {busy ? "Subiendo…" : url ? "Cambiar logo" : "Subir logo"}
            </button>
            {url && (
              <button type="button" onClick={remove} disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-neutral-200 text-neutral-600 hover:text-rose-600 disabled:opacity-50" data-testid="company-logo-remove-btn">
                <Trash size={14}/> Quitar
              </button>
            )}
          </div>
          <p className="text-[11px] text-neutral-500">PNG, JPG o WEBP · máx. 2 MB · se ajusta a 512×512 px</p>
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="company-logo-file-input"/>
        </div>
      )}
    </div>
  );
}
